'use client';

import { forwardRef, useContext, useEffect, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useMergeRefs } from '../../hooks/use-merge-refs';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { checkboxRecipe } from './styles/checkbox.styles';
import type { CheckboxPropsInterface } from './types/checkbox.types';

export const Checkbox = forwardRef<HTMLInputElement, CheckboxPropsInterface>(
  (props: CheckboxPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);

    const {
      className,
      css: cssProp,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      readOnly = formFieldContext.readOnly || false,
      required = formFieldContext.required || false,
      indeterminate = false,
      ...rest
    } = props;

    const checkboxRef = useRef<HTMLInputElement>(null);
    const ref = useMergeRefs([checkboxRef, forwardedRef]);

    const styles = css(checkboxRecipe.raw({ disabled, indeterminate }), cssProp);

    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate, ref]);

    return (
      <input
        aria-checked={indeterminate ? 'mixed' : undefined}
        aria-describedby={formFieldContext.descriptionId}
        aria-invalid={error ?? undefined}
        className={cx('c11n', styles, className)}
        data-c11n-component="Checkbox"
        data-c11n-version={__C11N_VERSION__}
        disabled={disabled}
        id={formFieldContext.controlId}
        type="checkbox"
        readOnly={readOnly}
        ref={ref}
        required={required}
        {...rest}
      />
    );
  },
);

Checkbox.displayName = 'Checkbox';
